<script lang="ts" setup>
import { ref, watch } from "vue";

import { getSvbSummaryData } from "@/services/api/chart.api";
import StackedAreaChart from "@/components/app/charts/StackedAreaChart.vue";
import { useToast } from "vue-toastification";
import { severityCode } from "@/services/api/httpClient";
import { useUserStore } from "@/stores/user";

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const chartData = ref([]);
const chartFields = [
  "forSale",
  "rentable",
  "minorMaintenance",
  "majorMaintenance",
  "demolish",
];
const chartColors = [
  'hsl(196 100% 63.9%)',
  'hsl(142 71% 65%)',
  'hsl(262 22% 87%)',
  'hsl(30 100% 60%)',
  'hsl(350 85% 60%)',
  'hsl(48 100% 60%)',
];

const isLoading = ref(true);
const hasError = ref(false);
const toaster = useToast();
const userStore = useUserStore();

const labelMap = {
  rentable: "Verhuur",
  forSale: "Verkoop",
  demolish: "Sloop",
  minorMaintenance: "Klein Onderhoud",
  majorMaintenance: "Groot Onderhoud",
};

const customLabelFn = (fieldName) => labelMap[fieldName] || fieldName;

watch(
  () => userStore.currentPartyReference,
  () => {
    fetchSvbSummaryData();
  },
  { immediate: true }
);

async function fetchSvbSummaryData() {
  isLoading.value = true;
  hasError.value = false;
  const response = await getSvbSummaryData(userStore.currentPartyReference);
  if (response && response.severity <= severityCode.warning) {
    chartData.value = response.data;
    isLoading.value = false;
  } else {
    isLoading.value = false;
    hasError.value = true;
    toaster.error("Ophalen van de SVB data is mislukt!");
  }
}
</script>
<template>
  <div class="widget-item" :class="{ 'widget-item--compact': props.compact }">
    <header>
      <h2>SVB Data</h2>
    </header>
    <loader-spinner v-if="isLoading" :is-loading="isLoading" :min-height="300"></loader-spinner>
    <section v-else-if="hasError" class="widget-wrapper" :min-height="300">
      <v-container class="pa-0">
        <p>Geen gegevens beschikbaar voor deze partij</p>
      </v-container>
    </section>
    <section v-else class="widget-wrapper" :min-height="300">
      <div class="chart-container">
        <StackedAreaChart
          :data="chartData"
          :fields="chartFields"
          :colors="chartColors"
          :label-fn="customLabelFn"
          :chart-style="{ width: '100%', height: '300px' }"
        />
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.widget-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 300px;
}

.chart-container {
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}
</style>
